import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Guid } from 'typescript-guid';
import { Contact } from '@omni/classes/contact/contact.class';
import { IONote } from '@omni/classes/io/io-note.class';
import {
  EXPENSE_STATUS_LABEL_KEYS,
  ExpenseMasterData,
  ExpenseOption,
  MeetingExpense,
  expenseStatus,
} from '@omni/classes/meeting-expense/meeting-expense.class';
import { MEETING_EXPENSE_AUTOSAVE_DEBOUNCE_MS } from '@omni/classes/meeting-expense/meeting-expense.constants';
import {
  ExpenseFieldErrors,
  INVALID_AMOUNT,
  canSubmitExpense,
  filterByWords,
  fromDateOnly,
  isExpenseAmountOutOfRange,
  isSubtypeRequired,
  shouldBlockCustomerSearch,
  stepExpenseAmount,
  subtypesForType,
  validateExpense,
} from '@omni/services/meeting-expense/meeting-expense.rules';
import { MeetingExpenseService } from '@omni/services/meeting-expense/meeting-expense.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { PENDING_NOTE_ID_PREFIX, isExpenseAttachmentBudgetError } from '@omni/classes/meeting-expense/meeting-expense.pending';
import { StagedSdrNote } from '@omni/data-services/sample/sample-delivery-request.data.service';
import { NotesAttachmentsConfig } from '@omni/utility/notes-attachments-config.util';
import { IndDropdownListComponent } from '@omni/components/shared/ind-dropdown-list/ind-dropdown-list';
import { IndDropdownListDetailModel } from '@omni/models/indDropdownListModel';
import { SdrNotesSectionComponent } from '@omni/components/sample/sdr-notes-section/sdr-notes-section.component';
import { CurViewPageType, DateTimeFieldType, IndDatetimeFormComponent } from '@omni/components/shared/ind-datetime-form/ind-datetime-form';
import { Section } from '@omni/components/shared/ind-form-list/ind-form-list.component';
import { FormFieldType, IndFormFieldViewDataModel } from '@omni/models/indFormFieldDataModel';
import { ActivityService } from '@omni/services/activity/activity.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';

type ExpenseField = keyof ExpenseFieldErrors;

@Component({
  selector: 'meeting-expense-form',
  templateUrl: 'meeting-expense-form.component.html',
  styleUrls: ['meeting-expense-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingExpenseFormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() expense: MeetingExpense;
  @Input() readOnly = false;
  @Input() contacts: Contact[] = [];
  @Input() masterData: ExpenseMasterData;
  @Input() currencyLabel = '';
  @Input() notesConfig: NotesAttachmentsConfig;
  @Output() expenseChange = new EventEmitter<MeetingExpense>();
  @Output() expenseSubmitted = new EventEmitter<MeetingExpense>();
  @ViewChild(SdrNotesSectionComponent) notesSection?: SdrNotesSectionComponent;
  @ViewChild('customerWrap', { static: true }) customerWrap: ElementRef<HTMLElement>;

  touched: Partial<Record<ExpenseField, boolean>> = {};
  contactFormField: IndFormFieldViewDataModel;
  amountText = '';
  seededNotes: StagedSdrNote[] = [];
  isSubmitting = false;

  private stagedNotes: StagedSdrNote[] = [];
  private isDestroyed = false;
  private readonly amountInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly expenseService: MeetingExpenseService,
    private readonly popoverCtrl: PopoverController,
    private readonly activityService: ActivityService,
    private readonly translate: TranslateService,
    private readonly notificationService: NotificationService,
    private readonly dateTimeFormats: DateTimeFormatsService,
    private readonly cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.amountText = this.expense.amount == null ? '' : String(this.expense.amount);
    this.buildContactField();
    this.seedNotes();
    this.amountInput$
      .pipe(debounceTime(MEETING_EXPENSE_AUTOSAVE_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => this.flushAmount());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The editable notes section is recreated when readOnly flips back; it must start from the current notes.
    if (changes['readOnly'] && !changes['readOnly'].firstChange && !this.readOnly) this.seedNotes();
    if ((changes['readOnly'] || changes['contacts']) && this.contactFormField) this.buildContactField();
  }

  ngAfterViewInit(): void {
    this.customerWrap.nativeElement.addEventListener('click', this.blockCustomerSearchWithoutContacts, true);
  }

  ngOnDestroy(): void {
    this.flushAmount();
    this.customerWrap.nativeElement.removeEventListener('click', this.blockCustomerSearchWithoutContacts, true);
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  get statusLabel(): string {
    return this.expense.statusLabel || this.translate.instant(EXPENSE_STATUS_LABEL_KEYS[expenseStatus(this.expense)]);
  }

  get showSubtype(): boolean {
    return isSubtypeRequired(this.masterData, this.expense.typeId);
  }

  get dateText(): string {
    return this.expense.expenseDate ? this.dateTimeFormats.formatDateForDisplay(fromDateOnly(this.expense.expenseDate)) : '';
  }

  errorFor(field: ExpenseField): string | null {
    if (this.readOnly || !this.touched[field]) return null;
    if (field === 'amount' && this.typedAmountOutOfRange) return INVALID_AMOUNT;
    return validateExpense(this.expense, this.masterData)[field] ?? null;
  }

  private get typedAmountOutOfRange(): boolean {
    return isExpenseAmountOutOfRange(this.parseAmount());
  }

  async openDatePicker(): Promise<void> {
    if (this.readOnly) return;
    const bounds = this.expenseService.dateBounds();
    const current = fromDateOnly(this.expense.expenseDate || bounds.max);
    this.activityService.dateTimePickerType = DateTimeFieldType.StartDateField;
    const popover = await this.popoverCtrl.create({
      component: IndDatetimeFormComponent,
      componentProps: {
        currentViewPage: CurViewPageType.MeetingExpense,
        startDateTimeValue: current,
        endDateTimeValue: current,
        earliestDate: bounds.min,
        latestDate: bounds.max,
      },
      cssClass: 'datetime-popover',
    });
    await popover.present();
    const { data } = await popover.onDidDismiss();
    this.touched.date = true;
    const picked = typeof data?.selectedDate === 'string' ? data.selectedDate.slice(0, 10) : '';
    if (picked && picked !== this.expense.expenseDate) this.applyChange({ expenseDate: picked });
    else this.cdRef.markForCheck();
  }

  onAmountInput(text: string): void {
    this.amountText = text;
    this.amountInput$.next(text);
  }

  get canDecrement(): boolean {
    return Number(this.amountText) > 0;
  }

  stepAmount(delta: number): void {
    if (this.readOnly) return;
    this.touched.amount = true;
    this.onAmountInput(String(stepExpenseAmount(this.amountText, delta)));
  }

  onAmountBlur(): void {
    this.touched.amount = true;
    this.flushAmount();
    this.cdRef.markForCheck();
  }

  async submit(): Promise<void> {
    if (this.isSubmitting || this.readOnly) return;
    this.flushAmount();
    // Text or a file still in the composer rides along, as it does for SDR.
    this.notesSection?.saveNewNote();
    this.touched = { date: true, type: true, subtype: true, amount: true };
    if (this.typedAmountOutOfRange || !canSubmitExpense(this.expense, this.masterData, this.notesConfig)) {
      this.notify('MEETING_EXPENSE_SUBMIT_INCOMPLETE');
      this.cdRef.markForCheck();
      return;
    }
    this.isSubmitting = true;
    this.cdRef.markForCheck();
    try {
      const submitted = await this.expenseService.submit(this.expense);
      this.expense = { ...this.expense, statusCode: submitted.statusCode, statusLabel: submitted.statusLabel, pendingSync: submitted.pendingSync };
      this.expenseChange.emit(this.expense);
      this.expenseSubmitted.emit(this.expense);
    } catch (err) {
      console.error('MeetingExpenseForm.submit failed', err);
      this.notify('MEETING_EXPENSE_SUBMIT_FAILED');
    } finally {
      this.isSubmitting = false;
      this.cdRef.markForCheck();
    }
  }

  async pickType(event: Event): Promise<void> {
    const picked = await this.pickOne(event, 'meeting-expense-type', this.masterData.types, this.expense.typeId);
    this.touched.type = true;
    if (picked === undefined || picked.id === this.expense.typeId) return this.cdRef.markForCheck();
    this.applyChange({ typeId: picked.id, typeName: picked.name, subtypeId: null, subtypeName: null });
  }

  async pickSubtype(event: Event): Promise<void> {
    const options = subtypesForType(this.masterData, this.expense.typeId);
    const picked = await this.pickOne(event, 'meeting-expense-subtype', options, this.expense.subtypeId);
    this.touched.subtype = true;
    if (picked === undefined || picked.id === this.expense.subtypeId) return this.cdRef.markForCheck();
    this.applyChange({ subtypeId: picked.id, subtypeName: picked.name });
  }

  onStagedNoteAdded(event: { index: number; note: StagedSdrNote }): void {
    const note = event.note;
    const clientId = note.clientId ?? Guid.create().toString();
    note.clientId = clientId;
    this.stagedNotes.splice(event.index, 0, note);
    this.expenseService.createNote(this.expense.id, clientId, note).then(
      (annotationId) => {
        if (!annotationId) return;
        note.existingAnnotationId = annotationId;
        if (!this.isDestroyed) this.syncNotes();
      },
      (err) => this.dropFailedNote(note, isExpenseAttachmentBudgetError(err) ? 'MEETING_EXPENSE_OFFLINE_ATTACHMENT_LIMIT' : 'MEETING_EXPENSE_SAVE_FAILED'),
    );
    this.syncNotes();
  }

  onStagedNoteEdited(event: { index: number; note: StagedSdrNote; attachmentCleared: boolean }): void {
    this.stagedNotes[event.index] = event.note;
    // A seeded attachment carries no body; resending it would blank the file on the server.
    const snapshot = { ...event.note, attachment: event.note.attachment?.documentBody ? event.note.attachment : undefined };
    this.expenseService.updateNote(this.expense.id, snapshot, event.attachmentCleared).catch((err) =>
      this.notify(isExpenseAttachmentBudgetError(err) ? 'MEETING_EXPENSE_OFFLINE_ATTACHMENT_LIMIT' : 'MEETING_EXPENSE_SAVE_FAILED'));
    this.syncNotes();
  }

  onStagedNoteRemoved(index: number): void {
    const [removed] = this.stagedNotes.splice(index, 1);
    if (!removed) return;
    this.expenseService.removeNote(this.expense.id, removed).catch(() => this.notifyFailure());
    this.syncNotes();
  }

  private seedNotes(): void {
    this.seededNotes = this.expense.notes.map((note) => {
      const isPending = note.noteId.startsWith(PENDING_NOTE_ID_PREFIX);
      return {
        noteText: note.noteText ?? '',
        existingAnnotationId: isPending ? undefined : note.noteId || undefined,
        clientId: isPending ? note.noteId.slice(PENDING_NOTE_ID_PREFIX.length) : undefined,
        attachment: note.hasDocument
          ? { documentBody: '', fileName: note.documentName ?? '', mimeType: note.documentMimeType ?? '', fileSize: note.documentSize ?? 0 }
          : undefined,
      };
    });
    this.stagedNotes = [...this.seededNotes];
    for (const note of this.seededNotes) {
      if (!note.clientId) continue;
      void this.expenseService.resolveAnnotationId(note.clientId).then((annotationId) => {
        if (!annotationId) return;
        note.existingAnnotationId = annotationId;
        if (!this.isDestroyed) this.syncNotes();
      });
    }
  }

  // Capture phase: ind-form-field opens its search from inner handlers, so the toast has to pre-empt them.
  private readonly blockCustomerSearchWithoutContacts = (event: Event): void => {
    if (!shouldBlockCustomerSearch(this.readOnly, this.contacts.length, this.expense.contactId)) return;
    event.stopPropagation();
    this.notificationService.notify(this.translate.instant('MEETING_EXPENSE_NO_MEETING_CUSTOMER'), 'Meeting Expenses', 'top', ToastStyle.INFO, 3000);
  };

  private contactItems(excludeId: string | null): { id: string; title: string; image: string; refObject: Contact }[] {
    return this.contacts
      .filter((contact) => contact.ID !== excludeId)
      .map((contact) => ({ id: contact.ID, title: contact.fullName, image: '', refObject: contact }));
  }

  private selectContact(contact: Contact | null): void {
    if (contact?.ID === this.expense.contactId) return;
    this.applyChange({ contactId: contact?.ID ?? null, contactName: contact?.fullName ?? null });
  }

  private buildContactField(): void {
    const selectedId = this.expense.contactId;
    const meetingContacts = this.contactItems(selectedId);
    const selected = selectedId ? [{ id: selectedId, title: this.expense.contactName ?? '', image: '', refObject: null, endIcon: 'closebutton' }] : [];
    const sections: Section[] = [
      { id: 'selected', title: this.translate.instant('SELECTED'), showCountOnTitle: true, maxResultsView: 5, bottomButtonShow: false, expand: false, showBottomButton: false, bottomButtonConfig: {}, data: selected },
      {
        id: 'results',
        title: this.translate.instant('AL_RESULTS'),
        showFilteredData: true,
        showCountOnTitle: true,
        maxResultsView: Math.max(meetingContacts.length, 5),
        bottomButtonShow: false,
        expand: true,
        showBottomButton: false,
        bottomButtonConfig: {},
        data: meetingContacts,
      },
    ];
    this.contactFormField = {
      label: this.translate.instant('CUSTOMER'),
      inputText: '',
      customPlaceholderLabel: selectedId ? '' : this.translate.instant('MEETING_EXPENSE_SELECT_CUSTOMER'),
      id: 'meeting-expense-contact-field',
      isReadOnly: true,
      showArrow: false,
      formFieldType: FormFieldType.LIST,
      isDisabled: this.readOnly,
      isSearchKeyRetain: true,
      hideInlineChip: !selectedId,
      modalConfiguration: {
        searchTitle: this.translate.instant('SEARCH_CONTACTS'),
        selectedTitle: this.translate.instant('SELECTED_CONTACTS'),
        searchInputConfig: { label: this.translate.instant('CONTACTS'), placeholder: this.translate.instant('TYPE') + '...' },
        showSearch: true,
      },
      filterMethod: (query: string) => filterByWords(this.contactItems(this.expense.contactId), query),
      endIcons: this.readOnly ? [] : [{ iconName: 'searchblue', overRideDisabledProperty: false }],
      sections,
      selectedData: selectedId ? selected : meetingContacts,
      eventHandler: (_id: string, _event: any, eventName: string, data: any) => {
        if (eventName === 'item-click' && data?.refObject) this.selectContact(data.refObject as Contact);
        else if (eventName === 'closebutton' && selectedId) this.selectContact(null);
      },
    };
  }

  private applyChange(changes: Partial<MeetingExpense>): void {
    if (this.readOnly) return;
    this.expense = { ...this.expense, ...changes };
    if ('contactId' in changes) this.buildContactField();
    this.expenseChange.emit(this.expense);
    this.expenseService.save(this.expense).catch(() => this.notifyFailure());
    this.cdRef.markForCheck();
  }

  private parseAmount(): number | null {
    const value = Number(this.amountText);
    return this.amountText.trim() === '' || !Number.isFinite(value) ? null : value;
  }

  // An out-of-range amount stays in the input with its error and is never saved.
  private flushAmount(): void {
    if (this.readOnly) return;
    const amount = this.parseAmount();
    if (isExpenseAmountOutOfRange(amount)) {
      this.touched.amount = true;
      this.cdRef.markForCheck();
      return;
    }
    if (amount !== this.expense.amount) this.applyChange({ amount });
  }

  private dropFailedNote(note: StagedSdrNote, messageKey: string): void {
    this.notify(messageKey);
    if (this.isDestroyed) return;
    const index = this.stagedNotes.indexOf(note);
    if (index < 0) return;
    // Deleting through the child keeps its staged list index-aligned with ours; it re-enters onStagedNoteRemoved.
    if (!this.readOnly && this.notesSection) {
      this.notesSection.deleteStagedNote(index);
      return;
    }
    this.stagedNotes.splice(index, 1);
    this.syncNotes();
  }

  // Keeps the parent's copy current so a collapsed-then-reopened row reseeds every note.
  private syncNotes(): void {
    const notes = this.stagedNotes.map((note) => {
      const original = this.expense.notes.find((existing) => !!existing.noteId && existing.noteId === note.existingAnnotationId);
      return new IONote({
        annotationid: note.existingAnnotationId ?? (note.clientId ? `${PENDING_NOTE_ID_PREFIX}${note.clientId}` : undefined),
        notetext: note.noteText,
        isdocument: !!note.attachment,
        filename: note.attachment?.fileName,
        filesize: note.attachment?.fileSize,
        mimetype: note.attachment?.mimeType,
        ownerName: original?.ownerName ?? this.expense.ownerName,
        createdon: original?.createdTime ?? new Date(),
      });
    });
    this.expense = { ...this.expense, notes };
    this.expenseChange.emit(this.expense);
    this.cdRef.markForCheck();
  }

  /** Resolves `undefined` when dismissed without a choice. */
  private async pickOne(event: Event, id: string, options: ExpenseOption[], selectedId: string | null): Promise<ExpenseOption | undefined> {
    const viewData: IndDropdownListDetailModel = {
      id,
      data: options.map((option) => ({ id: option.id, title: option.name, isSelected: option.id === selectedId })),
    };
    const popover = await this.popoverCtrl.create({ component: IndDropdownListComponent, componentProps: { viewData }, cssClass: 'subject-popover', event });
    await popover.present();
    const { data } = await popover.onDidDismiss();
    const selected = data?.selectedItems?.[0];
    return selected ? options.find((option) => option.id === selected.id) : undefined;
  }

  private notify(key: string): void {
    this.notificationService.notify(this.translate.instant(key), 'Meeting Expenses', 'top', ToastStyle.DANGER, 3000);
  }

  private notifyFailure(): void {
    this.notify('MEETING_EXPENSE_SAVE_FAILED');
  }
}
